import List from '@pelckmans/elementis/components/list';
import Pill from '@pelckmans/elementis/components/pill';
import { useTranslation } from 'react-i18next';
import { MaterialIcon } from '../../../pages/material/components/MaterialIcon';
import MaterialPills from '../../../pages/material/toc/components/MaterialPills';
import { buildGroupProps } from '../../../utils/elementisUtils';
import ButtonGroup from '@pelckmans/elementis/components/button-group';
import Button from '@pelckmans/elementis/components/button';
import DetailsIcon from '@pelckmans/elementis/icons/details';
import TrashIcon from '@pelckmans/elementis/icons/trash';
import { getMaterialLink, isMedialink, Material } from './materialUtils';
import { UserMaterial } from '../../../api/user-material';
import { Medialink } from '../../../api/medialink';

interface Props {
  material: Material;
  onOpenMaterial: (material: Material) => void;
  onDeleteMaterial: (material: Material) => void;
}

function getIcon(material: UserMaterial | Medialink) {
  if ('kind' in material) return <MaterialIcon materialType="medialink" contentType={material.contentType} />;
  return <MaterialIcon materialType="user-material" />;
}

function MaterialListItem({ material, onOpenMaterial, onDeleteMaterial }: Props) {
  const { t } = useTranslation();
  const link = getMaterialLink(material);

  const pillGroupProps = isMedialink(material)
    ? buildGroupProps(
        <>
          <Pill
            key="content-type-pill"
            icon={<MaterialIcon materialType="medialink" contentType={material.contentType} size="xs" />}
          >
            {material.contentType.name}
          </Pill>
          <MaterialPills
            key="material-pills"
            isStudent={material.group === 'student'}
            isTeacher={material.group === 'teacher'}
            isAdaptive={material.assessmentQExercise?.isAdaptive}
            isOnTheFly={material.assessmentQExercise?.isOnTheFly}
          />
        </>,
      )
    : buildGroupProps(
        <Pill key="content-type" icon={<MaterialIcon materialType="user-material" size="xs" />}>
          {t('CONTENT_TYPE_DESCRIPTIONS.OWN_MATERIAL')}
        </Pill>,
      );

  return (
    <List.Item
      key={material.id}
      title={material.name}
      icon={getIcon(material)}
      as="default"
      pillGroupProps={pillGroupProps}
      actionArea={
        <ButtonGroup
          {...buildGroupProps(
            <>
              {link ? (
                <Button
                  key="open-material"
                  startIcon={<DetailsIcon />}
                  as="link"
                  href={link}
                  target="_blank"
                  rel="noopener noreferrer"
                  prominence="neutral"
                  aria-label={t('SHARED_COMPONENTS.ADD_BUNDLE.MATERIAL_LIST.ITEM.OPEN.A11Y_LABEL')}
                />
              ) : (
                <Button
                  key="open-material"
                  startIcon={<DetailsIcon />}
                  as="button"
                  prominence="neutral"
                  onClick={() => onOpenMaterial(material)}
                  aria-label={t('SHARED_COMPONENTS.ADD_BUNDLE.MATERIAL_LIST.ITEM.OPEN.A11Y_LABEL')}
                />
              )}
              <Button
                key="trash"
                startIcon={<TrashIcon />}
                as="button"
                prominence="danger"
                onClick={() => onDeleteMaterial(material)}
                aria-label={t('SHARED_COMPONENTS.ADD_BUNDLE.MATERIAL_LIST.ITEM.DELETE.A11Y_LABEL')}
              />
            </>,
          )}
          size="sm"
        />
      }
    />
  );
}

export default MaterialListItem;
